// Phase A.2.b · Contact Detail → Linked Tours panel.
// Uses A.2.a canonical GET /api/contacts/{contact_id}/tours.

import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { contactApi } from "@/lib/entities";
import { roleLabel } from "@/lib/relationshipRoles";

const STATUS_LABEL = {
  inquiry: "Inquiry",
  hold: "Hold",
  booked: "Booked",
  confirmed: "Confirmed",
  in_progress: "In Progress",
  completed: "Completed",
  closed: "Closed",
};

export function ContactLinkedToursPanel({ contactId, testidPrefix = "contact-tours" }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(false);

  const load = async () => {
    setError(false);
    try {
      const d = await contactApi.tours(contactId, { page_size: 50 });
      setRows(d.results || []);
    } catch {
      setError(true);
      toast.error("Failed to load linked Tours");
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactId]);

  return (
    <div
      className="bg-white border border-coach-silver rounded-md"
      data-testid={`${testidPrefix}-panel`}
    >
      <div className="px-4 py-2 border-b border-coach-silver text-[11px] uppercase tracking-widest text-steel">
        Linked Tours
      </div>
      {rows === null && !error && (
        <div className="px-4 py-3 text-[12px] text-steel inline-flex items-center gap-1">
          <Loader2 size={12} className="animate-spin" /> Loading…
        </div>
      )}
      {error && (
        <div
          className="px-4 py-6 text-[13px] text-critical-red flex items-center justify-between"
          data-testid={`${testidPrefix}-error`}
        >
          Failed to load Tours.
          <button
            type="button"
            onClick={load}
            className="text-coach-blue hover:underline text-[12.5px]"
            data-testid={`${testidPrefix}-retry`}
          >
            Retry
          </button>
        </div>
      )}
      {rows && rows.length === 0 && (
        <div
          className="px-4 py-6 text-[13px] text-steel"
          data-testid={`${testidPrefix}-empty`}
        >
          Not linked to any Tours yet.
        </div>
      )}
      {rows && rows.length > 0 && (
        <ul className="divide-y divide-coach-silver/60">
          {rows.map((r) => {
            const t = r.tour || {};
            return (
              <li
                key={r.relationship_id}
                className="px-4 py-3 hover:bg-road-white cursor-pointer flex items-start justify-between gap-3"
                onClick={() => navigate(`/tours/${r.tour_id}`)}
                data-testid={`${testidPrefix}-row-${r.relationship_id}`}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    navigate(`/tours/${r.tour_id}`);
                  }
                }}
              >
                <div className="min-w-0 flex-1">
                  <div className="text-[13.5px] font-medium text-carbon truncate">
                    {t.display_name || `Tour ${t.tour_number || ""}`.trim() || "(unnamed tour)"}
                  </div>
                  <div className="text-[11.5px] text-steel truncate">
                    <span className="uppercase tracking-widest text-[10.5px] mr-2">
                      {t.tour_number || "—"}
                    </span>
                    {r.client?.display_name && <>{r.client.display_name} · </>}
                    {t.start_date && <>{t.start_date} → {t.end_date} · </>}
                    {t.status && <>{STATUS_LABEL[t.status] || t.status} · </>}
                    Role: {roleLabel(r.role)}
                  </div>
                </div>
                <ExternalLink size={13} className="text-coach-blue mt-1 shrink-0" aria-hidden="true" />
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default ContactLinkedToursPanel;
